import { useEffect, useState } from "react";
import {
  Game,
  GameData,
  TeamTurns,
  UpdateGameRequestModel,
} from "../../../domain/model/Game";
import { Category } from "../../../domain/model/Category";
import { Answer, Difficulty, Question } from "../../../domain/model/Question";
import { GetRandomQuestionUseCase } from "../../../domain/usecase/question/getRandomQuestion/GetRandomQuestionUseCase";
import { GetAnswerByQuestionIdUseCase } from "../../../domain/usecase/answer/getByQuestionId/GetByQuestionIdUseCase";
import { State } from "../../../domain/model/ResponseState";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  getDifficultyInfo,
  showGlobalLoadingView,
} from "../../../core/utils/Utils";
import { useNavigate } from "react-router-dom";
import { ROUTE_ENDPOINTS } from "../../../data/constant/RouteConstants";
import {
  getSoundCompletePath,
  SoundRepository,
  Sounds,
} from "../../../data/repository/sound/SoundRepository";
import { QuestionGiveAnswerUseCase } from "../../../domain/usecase/question/giveAnswer/QuestionGiveAnswerUseCase";
import { UpdateGameUseCase } from "../../../domain/usecase/game/update/UseCase";

export function GameViewModel(
  getRandomQuestionUseCase: GetRandomQuestionUseCase,
  getAnswerByQuestionIdUseCase: GetAnswerByQuestionIdUseCase,
  questionGiveAnswerUseCase: QuestionGiveAnswerUseCase,
  updateGameUseCase: UpdateGameUseCase,
) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [gameData, setGameData] = useState<GameData | undefined>();

  const navigate = useNavigate();

  const [question, setQuestion] = useState<Question | undefined>();
  const [answer, setAnswer] = useState<Answer | undefined>();

  const [selectedCategory, setSelectedCategory] = useState<
    Category | undefined
  >();
  const [selectedDifficulty, setSelectedDifficulty] = useState<
    Difficulty | undefined
  >();

  const [lastAnswerWasCorrect, setLastAnswerWasCorrect] = useState<
    boolean | undefined
  >();

  useEffect(() => {
    if (lastAnswerWasCorrect !== undefined) {
      setTimeout(() => {
        setLastAnswerWasCorrect(undefined);
      }, 1600);
    }
  }, [lastAnswerWasCorrect]);

  const soundRepo = new SoundRepository();

  function init(gameData: GameData) {
    setGameData(gameData);
  }

  function addOrDeductPointsFromATeam(
    isFirstTeam: boolean,
    add: boolean,
    point: number = 250,
  ) {
    if (!gameData) return;
    const scoreToAddOrDeduct = add ? point : -point;
    if (isFirstTeam) {
      updateGameData({
        firstTeamScore: gameData.firstTeamScore + scoreToAddOrDeduct,
      });
    } else {
      updateGameData({
        secondTeamScore: gameData.secondTeamScore + scoreToAddOrDeduct,
      });
    }
  }

  async function getRandomQuestion(category: Category, difficulty: Difficulty) {
    if (!gameData) return;

    setSelectedCategory(category);
    setSelectedDifficulty(difficulty);

    var localQuestion: Question | undefined;
    var localAnswer: Answer | undefined;

    if (!localQuestion || !localAnswer) {
      setLoading(true);

      const response = await getRandomQuestionUseCase.invoke({
        categoryId: category.id,
        difficulty: difficulty,
      });
      switch (response.responseState) {
        case State.Success:
          if (response.data) {
            localQuestion = response.data;
            if (localQuestion._id) {
              localAnswer = await getAnswerByQuestionId(localQuestion._id);
            }
          }
          break;
        case State.Fail:
        case State.Error:
          setLoading(false);
          showSnackbar(response.error?.message);
          return;
      }
    }

    setQuestion(localQuestion);
    setAnswer(localAnswer);
  }

  async function getAnswerByQuestionId(
    questionId: string,
  ): Promise<Answer | undefined> {
    const response = await getAnswerByQuestionIdUseCase.invoke(questionId);
    switch (response.responseState) {
      case State.Success:
        setAnswer(response.data);
        setLoading(false);
        return response.data;
      case State.Fail:
      case State.Error:
        await getAnswerByQuestionId(questionId);
        break;
    }
    setLoading(false);
  }

  async function OnQuestionAnswered({ who }: { who?: string }) {
    if (!gameData || !selectedDifficulty || !selectedCategory) return;

    var isAnsweredCorrectly = true;
    var pointToAdd = 0;
    var pointToDeduct = 0;

    if (who != undefined) {
      var pointsToAdd = getDifficultyInfo(
        selectedDifficulty,
        t,
        selectedCategory.schoolType,
      ).point;
      if (who == gameData.firstTeamName) {
        if (gameData.currentTeamTurn == TeamTurns.teamA) {
          pointsToAdd += pointToAdd;
        } else {
          pointToDeduct = 0;
        }
        updateGameData({
          firstTeamScore: gameData.firstTeamScore + pointsToAdd,
          secondTeamScore: gameData.secondTeamScore - pointToDeduct,
        });
      } else if (who == gameData.secondTeamName) {
        if (gameData.currentTeamTurn == TeamTurns.teamB) {
          pointsToAdd += pointToAdd;
        } else {
          pointToDeduct = 0;
        }
        updateGameData({
          secondTeamScore: gameData.secondTeamScore + pointsToAdd,
          firstTeamScore: gameData.firstTeamScore - pointToDeduct,
        });
      }
    } else {
      isAnsweredCorrectly = false;
    }
    markPointAsCompleted(gameData.currentTeamTurn);

    setTimeout(() => {
      setLastAnswerWasCorrect(isAnsweredCorrectly);
    }, 250);

    const easyDifficultyPoint = getDifficultyInfo(
      Difficulty.EASY,
      t,
      selectedCategory.schoolType,
    ).point;
    const mediumDifficultyPoint = getDifficultyInfo(
      Difficulty.MEDIUM,
      t,
      selectedCategory.schoolType,
    ).point;
    const hardDifficultyPoint = getDifficultyInfo(
      Difficulty.HARD,
      t,
      selectedCategory.schoolType,
    ).point;

    // sounds part
    const point = getDifficultyInfo(
      selectedDifficulty,
      t,
      selectedCategory.schoolType,
    ).point;
    if (isAnsweredCorrectly) {
      if (point == easyDifficultyPoint) {
        soundRepo.playWeightedSounds([
          {
            soundPath: getSoundCompletePath(Sounds.correct250QuestionCoin),
            weight: 33.0,
          },
          {
            soundPath: getSoundCompletePath(Sounds.correct250QuestionCorrect),
            weight: 33.0,
          },
          {
            soundPath: getSoundCompletePath(Sounds.correct250QuestionPopBells),
            weight: 34.0,
          },
        ]);
      } else if (point == mediumDifficultyPoint) {
        soundRepo.playWeightedSounds([
          {
            soundPath: getSoundCompletePath(Sounds.correct500QuestionAppMoney1),
            weight: 20.0,
          },
          {
            soundPath: getSoundCompletePath(Sounds.correct500QuestionAppMoney2),
            weight: 20.0,
          },
          {
            soundPath: getSoundCompletePath(
              Sounds.correct500QuestionCashMachine,
            ),
            weight: 30.0,
          },
          {
            soundPath: getSoundCompletePath(
              Sounds.correct500QuestionGlockSuccessDing,
            ),
            weight: 30.0,
          },
        ]);
      } else if (point == hardDifficultyPoint) {
        soundRepo.playWeightedSounds([
          {
            soundPath: getSoundCompletePath(
              Sounds.correct750QuestionMoneyCoins1,
            ),
            weight: 25.0,
          },
          {
            soundPath: getSoundCompletePath(
              Sounds.correct750QuestionMoneyCoins2,
            ),
            weight: 25.0,
          },
          {
            soundPath: getSoundCompletePath(Sounds.correct750QuestionPopWin1),
            weight: 25.0,
          },
          {
            soundPath: getSoundCompletePath(Sounds.correct750QuestionPopWin2),
            weight: 25.0,
          },
        ]);
      }
    } else {
      if (point == mediumDifficultyPoint || point == hardDifficultyPoint) {
        soundRepo.playWeightedSounds([
          {
            soundPath: getSoundCompletePath(Sounds.wrongAnswer2),
            weight: 20.0,
          },
          {
            soundPath: getSoundCompletePath(Sounds.wrongAnswer),
            weight: 25.0,
          },
          {
            soundPath: getSoundCompletePath(Sounds.wrongOption1),
            weight: 30.0,
          },
          { soundPath: getSoundCompletePath(Sounds.wrong), weight: 25.0 },
        ]);
      }
    }

    if (question?._id) {
      await giveAnswerToQuestion(question._id, isAnsweredCorrectly);
    }

    if (isGameFinished()) {
      finishGame(
        { id: gameData.resultGame?._id!, isCompleted: true },
        gameData,
      );
    }
  }

  async function finishGame(model: UpdateGameRequestModel, gameData: GameData) {
    showGlobalLoadingView(true);
    const response = await updateGameUseCase.invoke(model);
    showGlobalLoadingView(false);
    switch (response.responseState) {
      case State.Success:
        navigate(ROUTE_ENDPOINTS.GAME_FINISHED, {
          replace: true,
          state: {
            firstTeamName: gameData.firstTeamName,
            firstTeamScore: gameData.firstTeamScore,
            secondTeamName: gameData.secondTeamName,
            secondTeamScore: gameData.secondTeamScore,
            gameId: gameData.resultGame?._id,
          },
        });
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  async function giveAnswerToQuestion(questionId: string, isCorrect: boolean) {
    const response = await questionGiveAnswerUseCase.invoke(
      questionId,
      isCorrect,
    );
    switch (response.responseState) {
      case State.Success:
        break;
      case State.Fail:
      case State.Error:
        break;
    }
  }

  function isGameFinished(): boolean {
    if (!gameData) return false;
    var result = true;
    if (gameData.categories.length == 0) {
      return false;
    }

    gameData.categories.forEach((category) => {
      var hashmap = category.pointsHashmap;
      hashmap.values().forEach((m) => {
        m.values().forEach((m2) => {
          if (m2 == false) {
            result = false;
          }
        });
      });
    });

    return result;
  }

  function markPointAsCompleted(teamToGivePoint: TeamTurns) {
    if (!gameData || !selectedCategory || !selectedDifficulty) return;
    const categories = [...gameData.categories];
    const index = categories.findIndex((c) => c.id == selectedCategory.id);

    const currentCategory = categories[index];
    const currentTeamDiffMap =
      currentCategory.pointsHashmap.get(teamToGivePoint)!;
    currentTeamDiffMap?.set(selectedDifficulty, true);
    currentCategory.pointsHashmap.set(teamToGivePoint, currentTeamDiffMap);

    updateGameData({
      currentTeamTurn:
        gameData.currentTeamTurn == TeamTurns.teamA
          ? TeamTurns.teamB
          : TeamTurns.teamA,
    });
  }

  function setLoading(loading: boolean) {
    showGlobalLoadingView(loading);
  }

  function updateGameData({
    isLoading,
    categories,
    firstTeamName,
    secondTeamName,
    currentTeamTurn,
    firstTeamScore,
    secondTeamScore,
    resultGame,
  }: {
    isLoading?: boolean;
    categories?: Array<Category>;
    firstTeamName?: string;
    secondTeamName?: string;
    currentTeamTurn?: TeamTurns;
    firstTeamScore?: number;
    secondTeamScore?: number;
    resultGame?: Game;
  }) {
    if (!gameData) return;

    if (isLoading != undefined) {
      gameData.isLoading = isLoading;
    }

    if (categories != undefined) {
      gameData.categories = categories;
    }

    if (firstTeamName != undefined) {
      gameData.firstTeamName = firstTeamName;
    }

    if (secondTeamName != undefined) {
      gameData.secondTeamName = secondTeamName;
    }

    if (currentTeamTurn != undefined) {
      gameData.currentTeamTurn = currentTeamTurn;
    }

    if (firstTeamScore != undefined) {
      gameData.firstTeamScore = firstTeamScore;
    }

    if (secondTeamScore != undefined) {
      gameData.secondTeamScore = secondTeamScore;
    }

    if (resultGame != undefined) {
      gameData.resultGame = resultGame;
    }

    setGameData({ ...gameData });
  }

  function showSnackbar(
    message: string | undefined,
    variant: "error" | "success" = "error",
  ) {
    if (message) enqueueSnackbar(message, { variant: variant });
  }
  return {
    gameData,
    init,
    getRandomQuestion,
    answer,
    question,
    addOrDeductPointsFromATeam,
    OnQuestionAnswered,
    lastAnswerWasCorrect,
  };
}
